import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Lightbox from '../components/Lightbox';

function formatDate(m) {
  const raw = m.taken_at || m.created_at;
  if (!raw) return '';
  return new Date(raw).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Groups consecutive items sharing the same date into { label, entries: [{ item, i }] }
// buckets, keeping each entry's original index i into the full array (so lightbox
// navigation, which is index-based, still works across group boundaries).
function groupByDate(items) {
  const groups = [];
  let current = null;
  items.forEach((item, i) => {
    const label = formatDate(item) || 'Undated';
    if (!current || current.label !== label) {
      current = { label, entries: [] };
      groups.push(current);
    }
    current.entries.push({ item, i });
  });
  return groups;
}

const PW_KEY = 'dtmedia:pw';

function PasswordGate({ linkLabel, onSubmit, error, busy }) {
  const [password, setPassword] = useState('');
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(password); }}
        style={{ width: 280, textAlign: 'center' }}
      >
        <img src="/favicon.svg" alt="" width={40} height={40} style={{ borderRadius: 8, marginBottom: 12 }} />
        {linkLabel && <p style={{ margin: '0 0 4px', fontWeight: 600 }}>{linkLabel}</p>}
        <p style={{ marginBottom: 12 }}>This content is password-protected.</p>
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          style={{ width: '100%', marginBottom: 8 }}
        />
        <button type="submit" disabled={busy} style={{ width: '100%' }}>{busy ? 'Checking…' : 'Continue'}</button>
        {error && <p style={{ color: '#e66', marginTop: 8 }}>{error}</p>}
      </form>
    </div>
  );
}

export default function SharedGallery() {
  const { token } = useParams();
  const [media, setMedia] = useState(null);
  const [linkLabel, setLinkLabel] = useState('');
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [pw, setPw] = useState(() => localStorage.getItem(PW_KEY) || '');
  const [tab, setTab] = useState('photos');
  const [collectionId, setCollectionId] = useState('');
  const [search, setSearch] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    fetch(`/api/public/link-info?token=${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setLinkLabel(data.label || ''))
      .catch(() => {});
  }, [token]);

  const loadMedia = useCallback((pwToken) => {
    const qs = new URLSearchParams({ token });
    if (pwToken) qs.set('pw', pwToken);
    fetch(`/api/public/media?${qs}`)
      .then(async (r) => {
        if (r.status === 401) {
          const body = await r.json().catch(() => ({}));
          if (body.error === 'password_required') {
            setNeedsPassword(true);
            return null;
          }
        }
        if (!r.ok) throw new Error('invalid');
        return r.json();
      })
      .then((items) => {
        if (!items) return;
        setNeedsPassword(false);
        setMedia(items);
        // A link scoped to one collection (or one that just happens to contain
        // only one collection's worth of shared items) starts pre-filtered to it.
        const distinctCollections = new Set(items.map((m) => m.collection_id).filter(Boolean));
        if (distinctCollections.size === 1) {
          setCollectionId(String([...distinctCollections][0]));
        }
      })
      .catch(() => setError('This link is invalid or has been revoked.'));
  }, [token]);

  useEffect(() => { loadMedia(pw); }, [loadMedia, pw]);

  async function handlePasswordSubmit(password) {
    setPasswordBusy(true);
    setPasswordError('');
    try {
      const resp = await fetch('/api/public/verify-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'incorrect password');
      localStorage.setItem(PW_KEY, data.pw);
      setPw(data.pw);
      loadMedia(data.pw);
    } catch (e) {
      setPasswordError(e.message);
    } finally {
      setPasswordBusy(false);
    }
  }

  const collections = useMemo(() => {
    if (!media) return [];
    const seen = new Map();
    for (const m of media) {
      if (m.collection_id) seen.set(m.collection_id, m.collection_name);
    }
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [media]);

  const singleCollectionName = collections.length === 1 ? collections[0].name : null;
  const heading = singleCollectionName || linkLabel || "David Twaddell's Media Catalogue";
  const subtitle = singleCollectionName && linkLabel ? linkLabel : null;

  useEffect(() => {
    const parts = [linkLabel, singleCollectionName || "David Twaddell's Media Catalogue"].filter(Boolean);
    document.title = parts.join(' · ');
  }, [linkLabel, singleCollectionName]);

  const visible = useMemo(() => {
    if (!media) return [];
    let items = media;
    if (collectionId) items = items.filter((m) => String(m.collection_id) === String(collectionId));
    const term = search.trim().toLowerCase();
    if (term) {
      items = items.filter(
        (m) => m.original_filename.toLowerCase().includes(term) || (m.description || '').toLowerCase().includes(term)
      );
    }
    items = [...items];
    items.sort((a, b) => new Date(b.taken_at || b.created_at) - new Date(a.taken_at || a.created_at));
    return items;
  }, [media, collectionId, search]);

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>{error}</p>
      </div>
    );
  }
  if (needsPassword) {
    return <PasswordGate linkLabel={linkLabel} onSubmit={handlePasswordSubmit} error={passwordError} busy={passwordBusy} />;
  }
  if (!media) return null;

  const photos = visible.filter((m) => m.type === 'image');
  const audio = visible.filter((m) => m.type === 'audio');

  const assetParams = new URLSearchParams({ token });
  if (pw) assetParams.set('pw', pw);

  const photoItems = photos.map((m) => ({
    ...m,
    fullUrl: `/api/public/media/${m.id}/file?${assetParams}`,
    thumbUrl: m.has_thumb ? `/api/public/media/${m.id}/thumb?${assetParams}` : null,
  }));

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: subtitle ? 2 : 24 }}>
        <img src="/favicon.svg" alt="" width={28} height={28} style={{ borderRadius: 6, flexShrink: 0 }} />
        <h1 style={{ fontSize: 20, margin: 0 }}>{heading}</h1>
      </div>
      {subtitle && (
        <p style={{ margin: '0 0 24px 38px', fontSize: 13, color: '#888' }}>{subtitle}</p>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          onClick={() => setTab('photos')}
          style={{ background: tab === 'photos' ? '#6d5efc' : undefined, borderColor: tab === 'photos' ? '#6d5efc' : undefined }}
        >
          Photos ({photos.length})
        </button>
        <button
          onClick={() => setTab('music')}
          style={{ background: tab === 'music' ? '#6d5efc' : undefined, borderColor: tab === 'music' ? '#6d5efc' : undefined }}
        >
          Music ({audio.length})
        </button>

        <input
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ fontSize: 14, width: 200 }}
        />

        {collections.length > 1 && (
          <>
            <span style={{ fontSize: 13, color: '#888', marginLeft: 12 }}>Collection:</span>
            <select
              value={collectionId}
              onChange={(e) => setCollectionId(e.target.value)}
              style={{ fontSize: 13 }}
            >
              <option value="">All Collections</option>
              {collections.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {tab === 'photos' && (
        <div>
          {groupByDate(photoItems).map((group, gi) => (
            <div key={gi} style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#aaa', fontWeight: 600 }}>{group.label}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16 }}>
                {group.entries.map(({ item, i }) => (
                  <div key={item.id}>
                    <img
                      src={item.thumbUrl || item.fullUrl}
                      alt={item.description || ''}
                      onClick={() => setLightboxIndex(i)}
                      style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', display: 'block' }}
                    />
                    {item.description && (
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#aaa' }}>{item.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {photos.length === 0 && <p style={{ color: '#666' }}>No photos yet.</p>}
        </div>
      )}

      {tab === 'music' && (
        <div>
          {groupByDate(audio).map((group, gi) => (
            <div key={gi} style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#aaa', fontWeight: 600 }}>{group.label}</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {group.entries.map(({ item: m }) => (
                  <div key={m.id} style={{ borderBottom: '1px solid #333', paddingBottom: 16 }}>
                    {m.description && <p style={{ margin: '0 0 8px' }}>{m.description}</p>}
                    <audio controls style={{ width: '100%' }} src={`/api/public/media/${m.id}/file?${assetParams}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {audio.length === 0 && <p style={{ color: '#666' }}>No music yet.</p>}
        </div>
      )}

      {lightboxIndex !== null && (
        <Lightbox
          items={photoItems}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </div>
  );
}
